import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import { useApiMutation } from "@/api/hooks/useApiMutation";
import { validateDraft as validateDraftMutation } from "@/entities/DataAssociation/DataAssociation.mutations";
import clsx from "clsx";
import ValidationCheck from "./ValidationCheck";
import { useCallback, useEffect, useState } from "react";
import {
    DraftValidation,
    ValidationFormula,
} from "@/entities/DataAssociation/Draft.model";
import {
    derivedStatementTypesAtom,
    labelingContextViewAtom,
    draftSubmissionAtom,
} from "../../state";
import { useAtomValue, useSetAtom } from "jotai";
import { Spinner } from "@/ui/atoms/Spinner";
import { commitDraft as commitDraftMutation } from "@/entities/DataAssociation/DataAssociation.mutations";
import { StatementType } from "@/entities/DataAssociation/File.model";
import { useOnEnter } from "@/ui/hooks/useOnEnter";

export function DraftCommit() {
    const setContextView = useSetAtom(labelingContextViewAtom);
    const derivedStatementTypes = useAtomValue(derivedStatementTypesAtom);
    const [validations, setValidations] = useState<DraftValidation>({
        completeness: {
            status: "loading",
            messages: [],
        },
        accountNaming: {
            status: "loading",
            messages: [],
        },
        formulas: {
            status: "loading",
            messages: [],
        },
    });
    const isValid = !Object.values(validations).some(
        (validation) =>
            validation.status === "fail" || validation.status === "loading",
    );
    const {
        mutate: validate,
        isError,
        isPending: isValidatePending,
    } = useApiMutation(validateDraftMutation, {
        mutationKey: ["validateDraft"],
        onSuccess: (response) => {
            setValidations(response);
        },
    });
    const { mutate: commit, isPending } = useApiMutation(commitDraftMutation, {
        mutationKey: ["commitDraft"],
        onSuccess: () => {
            setContextView(null);
        },
    });
    const draftSubmission = useAtomValue(draftSubmissionAtom);

    useEffect(() => {
        validate({ draft: { rows: draftSubmission?.rows } });
    }, []);

    const onEnter = useCallback(() => {
        if (isError) {
            validate({ draft: { rows: draftSubmission?.rows } });
            return;
        }
        if (!isValid || isPending || isValidatePending) {
            return;
        }
        commit({ draft: { rows: draftSubmission?.rows } });
    }, [
        commit,
        draftSubmission,
        validate,
        isError,
        isValid,
        isPending,
        isValidatePending,
    ]);

    useOnEnter(onEnter);

    const defaultStatus = isValidatePending
        ? "loading"
        : isError
          ? "neutral"
          : undefined;

    return (
        <div
            className={clsx(
                "px-6",
                "absolute inset-0 flex min-h-0 flex-col overflow-hidden",
            )}
        >
            <div className="sticky my-6 flex flex-col">
                <RightSidebarButton />
                <h2 className="pageheading">Labeling Checklist</h2>
                <p className="body-s subdued">
                    The following checks must pass before the file can be marked
                    as labeled.{" "}
                </p>

                <p
                    className={clsx(
                        "body-micro subdued mt-1 text-red-400",
                        !isError && "invisible",
                    )}
                >
                    Something went wrong while validating the draft.
                </p>
            </div>

            <ul
                className={clsx(
                    "pb-30",
                    "flex flex-col",
                    "custom-scrollbar hide-scrollbar gap-4 overflow-y-auto",
                )}
            >
                <li>
                    <ValidationCheck
                        title="Completeness"
                        description="All rows must have an account name and label."
                        status={
                            defaultStatus || validations.completeness.status
                        }
                    />
                </li>
                <li>
                    <ValidationCheck
                        title="Account Naming"
                        description="All account names must be unique."
                        status={
                            defaultStatus || validations.accountNaming.status
                        }
                    />
                </li>

                {derivedStatementTypes.includes(
                    StatementType.BALANCE_SHEET,
                ) && (
                    <>
                        <li>
                            <ValidationCheck
                                title="Balance Sheet Balance"
                                description="Assets, Liabilities, and Equity must balance."
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.BALANCE_SHEET_BALANCE,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                    </>
                )}
            </ul>
            <div
                className={clsx(
                    "bg-default w-full p-6",
                    "absolute inset-x-0 bottom-0",
                )}
            >
                {!isError && (
                    <button
                        className="primary xl w-full"
                        disabled={!isValid || isPending}
                        onClick={() =>
                            commit({ draft: { rows: draftSubmission?.rows } })
                        }
                    >
                        Commit draft {isPending && <Spinner />}
                    </button>
                )}
                {isError && (
                    <button
                        className="primary xl w-full"
                        onClick={() =>
                            validate({ draft: { rows: draftSubmission?.rows } })
                        }
                        disabled={isValidatePending}
                    >
                        Retry validation {isValidatePending && <Spinner />}
                    </button>
                )}
            </div>
        </div>
    );
}
